import {t} from '@eitje/bridge'
import _ from 'lodash'
import Moment from 'moment'
import {DateRange, extendMoment} from 'moment-range'
import momentTz from 'moment-timezone'
import {areConsecutiveDates, exists, prettyPrintArray, sortDates, weekdays} from '../helpers'

const moment = extendMoment(Moment)
const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD'

Moment.defaultFormat = DEFAULT_DATE_FORMAT
Moment.defaultFormatUtc = DEFAULT_DATE_FORMAT

export const setTimezone = (tz = 'Europe/Amsterdam') => {
	if (tz == 'Amsterdam') tz = 'Europe/Amsterdam'
	momentTz.tz.setDefault(tz)
}

const timeFormats = [
	'YYYY-MM-DDTHH:mm:ss.SSSZ',
	'YYYY-MM-DDTHH:mm:ss.SSSSZ', // should probably not be required, but is currently needed for Operation.createdAt()
	'YYYY-MM-DDTHH:mm:ss',
	'YYYY-MM-DDTHH:mm',
	'YYYY-MM-DDTHH:mmZ',
	'DD-MM-YYYYTHH:mm:ss.SSSZ',
	'DD-MM-YYYYTHH:mm:ss',
	'DD-MM-YYYYTHH:mm',
	'DD-MM-YYYYTHH:mmZ',
	'HH:mm',
]

const DATE_YYYY_MM_DD = 'YYYY-MM-DD'
const DATE_DD_MM_YYYY = 'DD-MM-YYYY'
const DATE_HH_MM = 'YYYY-MM-DD HH:mm'
const DATE_YYYY_MM_DD_SLASH = 'YYYY/MM/DD'
const DATE_DD_MM_YYYY_SLASH = 'DD/MM/YYYY'
const TIME_YYYY = ['YYYY-MM-DDTHH:mm:ss.SSSZ', 'YYYY-MM-DDTHH:mm:ss.SSSSZ', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm', 'YYYY-MM-DDTHH:mmZ']
const TIME_DD = ['DD-MM-YYYYTHH:mm:ss.SSSZ', 'DD-MM-YYYYTHH:mm:ss', 'DD-MM-YYYYTHH:mm', 'DD-MM-YYYYTHH:mmZ']
const DATE_FALLBACK_FORMATS = [DATE_YYYY_MM_DD, DATE_DD_MM_YYYY, DATE_HH_MM, DATE_YYYY_MM_DD_SLASH, DATE_DD_MM_YYYY_SLASH]

export function createMoment(date) {
	if (date == null || date === '') return moment()

	// fast path
	if (moment.isMoment(date)) return date

	if (typeof date === 'string') {
		// there are a few places where we are creating faulty date strings
		// for instance when we are creating a new shift in with the `shiftMapper` function.
		if (date.startsWith('undefinedT')) return createMoment(date.substring(10))

		// cheapest first
		if (isTimeString(date)) return moment(date, 'HH:mm', true)

		// date-only: YYYY-MM-DD
		if (date.length === 10 && date.charCodeAt(4) === 45 && date.charCodeAt(7) === 45) return moment(date, DATE_YYYY_MM_DD, true)

		// date-only: DD-MM-YYYY
		if (date.length === 10 && date.charCodeAt(2) === 45 && date.charCodeAt(5) === 45) return moment(date, DATE_DD_MM_YYYY, true)

		// datetime: ISO-ish => try strict ISO first
		if (date.includes('T')) {
			const mIso = moment(date, moment.ISO_8601, true)
			if (mIso.isValid()) return mIso

			// fallback: small family based on prefix
			const fmts = date.charCodeAt(4) === 45 ? TIME_YYYY : TIME_DD
			return moment(date, fmts, true)
		}

		// last resort (keep small)
		return moment(date, DATE_FALLBACK_FORMATS, true)
	}

	return moment(date)
}

const OPEN_END = moment('2050-01-01')

export function makeRange(start, end) {
	const startMoment = createMoment(start)
	const endMoment = end ? createMoment(end) : OPEN_END.clone()
	// we used to make this an open range, but it seems moment-tz conflicts with moment-range in this aspect.
	// resulting in an invalid end date and thus not responding properly to contains etc
	if (endMoment.isBefore(startMoment)) {
		endMoment.add(1, 'day')
	}
	if (start == end && _.isString(start) && !endMoment.isDateTime()) {
		endMoment.endOf('day')
	}
	return moment.range(startMoment, endMoment)
}

export function makeDayRange(start) {
	return makeRange(createMoment(start).startOf('day'), createMoment(start).endOf('day'))
}

export function makeYearRange(year) {
	const start = moment().set({year}).startOf('year')
	const end = start.clone().endOf('year')
	return makeRange(start, end)
}

export function rangeFromStart({start, length = 7, kind = 'day'} = {}) {
	const startMoment = createMoment(start)
	const endMoment = startMoment
		.clone()
		.add(length - 1, kind)
		.endOf('day')

	return moment.range(startMoment, endMoment)
}

export function dateArrayFromStart({start, length = 7, kind = 'day'} = {}) {
	const range = rangeFromStart({start, length, kind})
	return Array.from(range.by(kind))
}

export function _dateArrayFromStart({start, length = 7, kind = 'day'} = {}) {
	const range = rangeFromStart({start: createMoment(start), length, kind})
	return Array.from(range.by(kind))
}

export function rangeToDateArray(range, kind = 'day') {
	return Array.from(range.by(kind))
}

export function rangeForMonth({date}) {
	return rangeForPeriod({date, period: 'month'})
}

export function rangeForWeek({date}) {
	return rangeForPeriod({date, period: 'week'})
}

export function weekToDateRange({year, week}) {
	const date = createMoment().isoWeekYear(year).isoWeek(week)
	return rangeForWeek({date})
}

export function rangeForYear({date}) {
	return rangeForPeriod({date, period: 'year'})
}

export function rangeForPeriod({date, period}) {
	date = createMoment(date)
	const range = makeRange(date.clone().startOf(period), date.clone().endOf(period))
	return range
}

export function dateArrayForMonth({date, kind = 'day'}) {
	const range = rangeForMonth({date})
	return Array.from(range.by(kind))
}

export function dateToString(_date) {
	return Moment.isMoment(_date) ? _date.format() : _date
}

export function identicalPeriod(startDate_1, endDate_1, startDate_2, endDate_2) {
	const range_1 = [dateToString(startDate_1), dateToString(endDate_1)]
	const range_2 = [dateToString(startDate_2), dateToString(endDate_2)]

	return _.isEqual(range_1, range_2)
}

const timeStringRegex = /^\d{1,2}:\d{2}$/ // accepts both 09:00 and 9:00, but not 9:0

const isTimeString = date => {
	return date?.match && date.match(timeStringRegex)
}

export const likeDateTime = val => {
	if (!exists(val)) return
	if (typeof val === 'number') return
	const d = moment(val, timeFormats)
	// should HH:mm be in formats? It's not really parsable..
	return d._f != 'HH:mm' && d.isDateTime() && d.isValid()
}

export function buildDateTime({date, time}) {
	const format = 'YYYY-MM-DD HH:mm'
	const _date = createMoment(date).format()
	return createMoment(`${_date} ${time}`).format(format) // we want it to be always YYYY-MM-DD when it comes out
}

export function formatMultipleDates(dates) {
	if (!dates || dates.length === 0) return '...'
	if (dates.length === 1) return date(dates[0]).ftime('fullDayMonth')

	const sorted = sortDates(dates)

	if (areConsecutiveDates(sorted)) {
		const start = date(sorted[0]).ftime('dayOfWeekMonth')
		const end = date(sorted[sorted.length - 1]).ftime('dayOfWeekMonth')
		return `${start} - ${end}`
	}

	const parts = sorted.map(currDate => date(currDate).ftime('dayMonth').replace('.', ''))
	return prettyPrintArray({array: parts, quotes: false, joinWord: '&'})
}

export const formatPeriodWithMonth = ({startDate, endDate}) => {
	const sameMonth = date(startDate).month() === date(endDate).month()

	if (sameMonth) {
		return `${startDate.date()} - ${endDate.ftime('dayMonth')}`
	} else {
		return `${startDate.ftime('dayMonth')} - ${endDate.ftime('dayMonth')}`
	}
}

export const formatDateRange = dateRange => {
	if (dateRange.length === 0) return '...'

	const [startDate, endDate = startDate] = dateRange

	if (startDate.isSame(endDate, 'day')) return startDate.ftime('fullDayMonthYear')

	return `${startDate.ftime('dayMonth')} - ${endDate.ftime('dayMonth')}`
}

/**
 * Formats the period containing a date as a week range, month and year, or quarter and year.
 * @param {import('moment').Moment} date Date within the period to format.
 * @param {'week' | 'month' | 'quarter'} periodType Period represented by the date.
 * @returns {string} Formatted period label.
 */
export const formatDatePeriod = (date, periodType) => {
	if (periodType === 'quarter') return `Q${date.quarter()} '${date.format('YY')}`
	if (periodType === 'week') {
		const {start, end} = rangeForPeriod({date: date, period: periodType})

		return formatDateRange([start, end])
	}

	return date.ftime('monthYear')
}

DateRange.prototype.days = function () {
	const end = this.end.clone().endOf('day')
	const start = this.start.clone().startOf('day')
	const diffInDays = end.diff(start, 'days')
	return diffInDays
}

DateRange.prototype.isValid = function () {
	return this.start.isValid()
}

DateRange.prototype.map = function (fn) {
	return [this.start, this.end].map(d => fn(d))
}

DateRange.prototype.hours = function () {
	const hours = this.end.diff(this.start, 'hours')
	return hours
}

DateRange.prototype.find = function (fn) {
	return rangeToDateArray(this).find(fn)
}

DateRange.prototype.toArray = function () {
	return rangeToDateArray(this)
}

DateRange.prototype.minutes = function () {
	const minutes = this.end.diff(this.start, 'minutes')
	return minutes
}

DateRange.prototype.isSingleDay = function () {
	if (exists(this.hours())) return this.hours() <= 24
}

DateRange.prototype.hasToday = function () {
	return this.contains(createMoment())
}

DateRange.prototype.overlapFactor = function (range) {
	const overlappingMinutes = this.intersect(range)?.diff('minutes') || 0
	const overlapFactor = overlappingMinutes / this.diff('minutes')
	return overlapFactor
}

DateRange.prototype.changeDay = function (date) {
	// In case the end of the shift falls on the next day, adjust the new end date accordingly
	const startEndDiff = this.days()
	return {
		start: moment(buildDateTime({date, time: this.start.formatTime()})),
		end: moment(buildDateTime({date: moment(date).add(startEndDiff, 'days'), time: this.end.formatTime()})),
	}
}

DateRange.prototype.format = function (format = 'dateTime') {
	return `${this.start.ftime(format)} ${t('common.t/m')} ${this.end.ftime(format)}`
}

DateRange.prototype.formatDate = function (format = 'date') {
	if (this.isSingleDay()) return this.start.ftime(format)
	return `${this.start.ftime(format)} ${t('common.t/m')} ${this.end.ftime(format)}`
}

DateRange.prototype.isDateRange = true

moment.fn.isToday = function () {
	return this.isSame(new Date(), 'day')
}

moment.fn.formatWeek = function () {
	return this.format("W ('YY)")
}

moment.fn.isYesterday = function () {
	const yesterday = moment().subtract(1, 'days').startOf('day')
	return this.isSame(yesterday, 'day')
}

moment.fn.isDateTime = function () {
	return timeFormats.includes(this._f)
}

moment.fn.formatDateTime = function (format = 'YYYY-MM-DD HH:mm') {
	return this.format(format)
}

moment.fn.formatDate = function ({year, day = true, month = true, time} = {}) {
	const parts = []
	if (day) parts.push('DD')
	if (month) parts.push('MMM')
	if (year) parts.push(`'YY`)

	let formatStr = parts.join(' ')
	if (time) formatStr += ', HH:mm'

	return this.format(formatStr).trim()
}

moment.fn.prevWeekday = function (n) {
	if (n > this.weekday()) this.subtract(1, 'week')
	return this.weekday(n)
}

moment.fn.nextWeekday = function (n) {
	if (n < this.weekday()) this.add(1, 'week')
	return this.weekday(n)
}

moment.fn.weekdayName = function () {
	return weekdays[this.weekday()]
}

moment.fn.prevMonthday = function (n) {
	if (n > this.date()) this.subtract(1, 'month')
	return this.date(n)
}

moment.fn.nextMonthday = function (n) {
	if (n < this.date()) this.add(1, 'month')
	return this.date(n)
}

moment.fn.inFuture = function () {
	return this > moment()
}

moment.fn.inPast = function () {
	return !this.inFuture()
}

moment.fn.formatTime = function () {
	return this.format('HH:mm')
}

const date = _date => {
	if (_.isString(_date)) return createMoment(_date)
	return createMoment(_date)
}

export {date, moment}
